import React from 'react'
import PropTypes from 'prop-types'
import { autorun, reaction } from 'mobx'
import { observer, inject } from 'mobx-react'
import { debounce, get, orderBy, uniqBy } from 'lodash'
import {  T } from 'components/misc'
import {
  MESSAGE_TYPE_UPGRADE,
  MESSAGE_TYPE_WARNING,
  MESSAGE_TYPE_SYSTEM,
  MESSAGE_TYPE_CONTACT_INFO,
  MESSAGE_TYPE_IMAGE,
  MESSAGE_TYPE_VOICE,
  MESSAGE_TYPE_GIFT_REVEAL,
  MODES,
} from 'core/constants'
import { MessageWarning } from './messages/SystemMessages'
import MessageDefault from './messages/MessageDefault'
import MessageImage from './messages/MessageImage'
import MessageVoice from './messages/MessageVoice'
import MessageGiftReveal from './messages/MessageGiftReveal'
import TypingIndicator from './TypingIndicator'
import SystemMessagesContainer, { TempSystemMessagesContainer } from './messages/SystemMessagesContainer'
import cn from 'classnames'
@inject('store')
@observer
class ChatContent extends React.Component {

  // Reference to the DOM
  node = null
  oldestSentAt = null
  loading = false

  get lastMessageDOMElement() {
    return document.querySelector(`.message-wrapper[data-id="${this.oldestSentAt}"]`)
  }

  componentDidMount() {
    this.mounted = true
    const { store } = this.props
    this.autoScroll(store.conversation)
    this.watchWidgetState()
    this.disposeConversationChange = reaction(
      () => store.conversation.conversationUid, // scroll to bottom when conversation changes
      () => {
        this.scrollToBottom({ fade: true })
        this.autoScroll(store.conversation)
      }
    )
    this.disposeScrollToMessage = reaction(
      () => store.scrollToMessageId,
      messageId => {
        if (!messageId) { return }
        setTimeout(() => {
          const el = this.node && this.node.querySelector(`[data-message-id="${messageId}"]`)
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            store.scrollToMessageId = null
            el.classList.add('message-highlighted')
            setTimeout(() => el.classList.remove('message-highlighted'), 2500)
          }
        }, 300)
      }
    )
  }

  componentDidUpdate(prevProps) {
    if (prevProps?.conversation.conversationUid !== this.props.conversation?.conversationUid) {
      prevProps.conversation.removeTempMessage()
    }
  }

  autoScroll = conversation => {
    // Dispose if previous observer is present
    if (this.dispose) {
      this.dispose()
    }
    // When a new message is received
    this.dispose = autorun(() => {
      // The "if" here allows autorun to observe the changes
      if (conversation && conversation.messages && conversation.messages.length) {
        // This check should be inside the "if"
        if (!this.loading) {
          this.scrollToBottom()
        }
      }
    })
  }

  watchWidgetState = () => {
    const { store } = this.props
    this.disposeWidgetStateChange = reaction(
      () => store.widgetState.isOpen,
      isOpen => {
        if (isOpen) {
          this.scrollToBottom()
        }
      })
  }

  componentWillUnmount() {
    this.mounted = false
    this.dispose()
    this.disposeWidgetStateChange()
    this.disposeConversationChange()
    this.disposeScrollToMessage()
  }

  scrollToBottom = ({ fade } = {}) => {
    if (fade) {
      this.node.style.transition = ''
      this.node.style.opacity = 0
    }
    setTimeout(() => {
      if (this.node && typeof(this.node.scrollHeight) !== 'undefined') {
        this.node.scrollTop = this.node.scrollHeight - this.node.clientHeight - 2
      }
      if (fade) {
        this.node.style.transition = '0.3s opacity 0.1s'
        this.node.style.opacity = 1
      }
    }, 50)
  }

  scrollToLastVisibleMessage = () => {
    if (this.props.store.conversation.oldestSentAt < this.oldestSentAt) {
      if (this.lastMessageDOMElement) {
        this.node.scrollTop = this.lastMessageDOMElement.offsetTop
      }
    }
  }

  handleScroll = e => {
    const percentScroll = Math.round(e.target.scrollTop / e.target.scrollHeight * 100)

    this.readConversation()

    if (percentScroll < 10) {
      this.loadMoreMessages()
    }
  }

  readConversation = debounce(() => {
    const { conversation } = this.props.store
    conversation.readConversation()
  }, 500)

  loadMoreMessages = debounce(() => {
    const { conversation } = this.props.store

    if (conversation.hasMoreMessages) {
      this.oldestSentAt = conversation.oldestSentAt
      this.loading = true
      conversation.fetchMessages(this.oldestSentAt).then(() => {
        // Check if component is still mounted before updating
        if (this.mounted) {
          this.loading = false
          this.scrollToLastVisibleMessage()
          this.forceUpdate()
        }
      })
    }
  }, 500)

  render() {
    const { conversation, user } = this.props.store
    const { features, mode } = this.props.options
    const users = { me: conversation.meProfile, other: conversation.otherUserProfile }
    let messages = uniqBy(orderBy(get(conversation, 'messages'), ['sentAt', 'id'], ['asc', 'asc']), 'id')
    messages = messages
      .filter(({type}) => type !== MESSAGE_TYPE_SYSTEM && type !== MESSAGE_TYPE_UPGRADE)
      .filter(({message, type}) => type !== 'message' || message.trim())

      if (mode === MODES.CHAT_LIST && messages.length > 0) {
        messages = messages.map((message, index, arr) => {
          const nextMessage = arr[index + 1];
          const shouldShowTimeStamp = !nextMessage || message.sentBy !== nextMessage.sentBy;
          return {
            ...message,
            shouldShowTimeStamp,
          };
        });
      }

    const shouldShowBlur = features.featuredFunctionality && this.props.store.conversation?.featuredApUnlock?.isChatLocked;

    return (
      <>
        <div className={cn('chat-conversation', { blurred: shouldShowBlur})} ref={c => (this.node = c)} onScroll={this.handleScroll}>
          {features.virtualHottie && <div className="ccp-virtual-hottie"><T>virtualHottie.chattingWithHottie</T> {users.other.username}</div>}
          {this.loading && (
            <div className="chat-loading-wrapper" key="chat-loading-block">
              <div className="chat-loading chat-loading-0"/>
              <div className="chat-loading chat-loading-1"/>
              <div className="chat-loading chat-loading-2"/>
            </div>
          )}
          {messages.map(message => {
            //TODO: revisit with KEVIN
            const isCorrespondent = [message.receiver, message.sentBy].includes(conversation.meIndex) || message.receiver == undefined && message.sentBy == undefined
            const props = { key: message.id, message, isCorrespondent, conversation }
            let rendered
            switch (message.type) {
              case MESSAGE_TYPE_CONTACT_INFO: {
                rendered = <MessageWarning {...props}/>
                break
              }
              case MESSAGE_TYPE_WARNING: {
                rendered = <MessageWarning {...props}/>
                break
              }
              case MESSAGE_TYPE_IMAGE: {
                rendered = <MessageImage {...props} users={users} user={user} conversationUid={conversation.conversationUid}/>
                break
              }
              case MESSAGE_TYPE_VOICE: {
                rendered = <MessageVoice {...props} users={users} user={user} conversationUid={conversation.conversationUid}/>
                break
              }
              case MESSAGE_TYPE_GIFT_REVEAL: {
                rendered = <MessageGiftReveal {...props} users={users} user={user} conversationUid={conversation.conversationUid}/>
                break
              }
              default:
                rendered = <MessageDefault {...props} users={users} user={user} shouldShowTimeStamp={message?.shouldShowTimeStamp} options={this.props.options}/>
            }
            return <div key={message.id} data-message-id={message.id}>{rendered}</div>
          })}
          <SystemMessagesContainer/>
          {conversation.lastMessageSentAt > 0 && <TypingIndicator conversationUid={conversation.conversationUid} showUntil={conversation.typingIndicator} scrollToBottom={this.scrollToBottom} user={users.other}/>}
          <TempSystemMessagesContainer scrollToBottom={this.scrollToBottom}/>
        </div>
      </>
    )
  }
}

ChatContent.propTypes = {
  conversation: PropTypes.object,
}

export default ChatContent
